/**
 * React Query hooks for Proposals API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '../lib/api';
import type { ProposalStatus, ProposalListResponse, ProposalDetailResponse } from '../types/api';

const PROPOSAL_KEYS = {
  all: ['proposals'] as const,
  list: (filters?: {
    workspaceId?: string;
    status?: ProposalStatus[];
    templateId?: string;
    limit?: number;
    offset?: number;
  }) => [...PROPOSAL_KEYS.all, 'list', filters ?? {}] as const,
  detail: (id: string) => [...PROPOSAL_KEYS.all, 'detail', id] as const,
};

export function useProposals(filters?: {
  workspaceId?: string;
  status?: ProposalStatus[];
  templateId?: string;
  limit?: number;
  offset?: number;
}, options?: {
  enabled?: boolean;
}) {
  return useQuery<ProposalListResponse>({
    queryKey: PROPOSAL_KEYS.list(filters),
    queryFn: () => getApiClient().getProposals(filters),
    staleTime: 1000 * 30,
    enabled: options?.enabled ?? true,
  });
}

export function useProposalDetail(proposalId: string | null) {
  return useQuery<ProposalDetailResponse>({
    queryKey: PROPOSAL_KEYS.detail(proposalId ?? ''),
    queryFn: () => getApiClient().getProposalDetail(proposalId!),
    enabled: !!proposalId,
    staleTime: 1000 * 30,
  });
}

export function useApproveProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { id: string; reason?: string }) =>
      getApiClient().approveProposal(params.id, params.reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: PROPOSAL_KEYS.all });
      queryClient.invalidateQueries({ queryKey: PROPOSAL_KEYS.detail(variables.id) });
    },
  });
}

export function useRejectProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { id: string; reason?: string }) =>
      getApiClient().rejectProposal(params.id, params.reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: PROPOSAL_KEYS.all });
      queryClient.invalidateQueries({ queryKey: PROPOSAL_KEYS.detail(variables.id) });
    },
  });
}
